import { useMemo, useState } from "react";
import { Section, H2, P, Bullets, Figure, Callout } from "./Primitives";
import { LineChart, Legend } from "./Charts";
import { eur, MONTHS } from "../../data/model";
import { simulate, zone, RECOMMENDED } from "../../data/simulator";

const MAX = 4000;
const MARKS = [
  { v: 1600, l: "Prudente" },
  { v: RECOMMENDED, l: "Consigliato" },
  { v: 3400, l: "Aggressivo" },
];
const labels = Array.from({ length: 24 }, (_, i) => `${MONTHS[i % 12]} '${i < 12 ? 27 : 28}`);

const Result = ({ label, value, note, tone, testId }) => (
  <div className={`sim-result sim-result--${tone || "base"}`} data-testid={testId}>
    <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--ink-soft)]">{label}</div>
    <div className="font-display text-2xl sm:text-3xl text-navy mt-2 leading-tight whitespace-nowrap">{value}</div>
    {note && <div className="text-xs text-[var(--ink-soft)] mt-1.5 leading-snug">{note}</div>}
  </div>
);

const Controls = ({ M, setM, opt, setOpt }) => (
  <div className="sim-controls avoid-break">
    <div className="flex flex-wrap items-center gap-3 mb-6">
      <span className="text-sm font-medium text-navy">Opzione scelta:</span>
      {["B", "A"].map((o) => (
        <button key={o} data-testid={`sim-option-${o}`} onClick={() => setOpt(o)} className={`sim-pill ${opt === o ? "sim-pill--on" : ""}`}>
          Opzione {o} {o === "B" ? "· partner a performance" : "· chiavi in mano"}
        </button>
      ))}
    </div>
    <div className="flex justify-between items-end gap-4">
      <label htmlFor="sim-budget" className="text-sm font-medium text-navy">Quanto investite in pubblicità ogni mese?</label>
      <span className="font-display text-4xl text-navy whitespace-nowrap" data-testid="sim-budget-value">{eur(M)}</span>
    </div>
    <input id="sim-budget" data-testid="sim-budget-slider" type="range" min="0" max={MAX} step="100" value={M} onChange={(e) => setM(Number(e.target.value))} className="sim-range mt-4" style={{ "--p": `${(M / MAX) * 100}%` }} />
    <div className="sim-zones" aria-hidden="true">
      <span style={{ width: "30%" }} className="bg-[#C9826B]">KO</span>
      <span style={{ width: "5%" }} className="bg-[#D9B77A]" title="Zona a rischio" />
      <span style={{ width: "40%" }} className="bg-[#9FAE8A]">Consigliata</span>
      <span style={{ width: "25%" }} className="bg-[#8796B3]">Veloce</span>
    </div>
    <div className="flex flex-wrap gap-2 mt-4">
      {MARKS.map((m) => (
        <button key={m.v} data-testid={`sim-preset-${m.l.toLowerCase()}`} onClick={() => setM(m.v)} className={`sim-pill ${M === m.v ? "sim-pill--on" : ""}`}>
          {m.l} · {eur(m.v)}
        </button>
      ))}
    </div>
  </div>
);

const useSimulator = () => {
  const [M, setM] = useState(RECOMMENDED);
  const [opt, setOpt] = useState("B");
  const r = useMemo(() => simulate(M, opt), [M, opt]);
  const ref = useMemo(() => simulate(RECOMMENDED, opt), [opt]);
  const z = zone(M, r.adsPayback);
  const series = [
    { name: `La vostra scelta: ${eur(M)}/mese`, values: r.adsSeries, color: z.key === "ko" ? "#B5563F" : "#B08D57", bold: true, marker: r.adsPayback },
    ...(M !== RECOMMENDED ? [{ name: `Piano consigliato: ${eur(RECOMMENDED)}/mese`, values: ref.adsSeries, color: "#14213D", dash: "5 4" }] : []),
  ];
  return { M, setM, opt, setOpt, r, ref, z, series };
};

const round100 = (v) => eur(Math.round(v / 100) * 100);

const paybackTile = (r, z, M) => {
  if (r.adsPayback) return { tone: z.key === "rischio" ? "warn" : "ok", value: `al ${r.adsPayback}° mese`, note: "da questo mese in poi è guadagno" };
  if (M === 0) return { tone: "ko", value: "Nessuna", note: "nessuna pubblicità attiva" };
  return { tone: "ko", value: "Mai (KO)", note: "entro 24 mesi i soldi non tornano" };
};

const Results = ({ r, ref, z, M }) => {
  const p = paybackTile(r, z, M);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 my-8">
      <Result testId="sim-ads-payback" tone={p.tone} label="La pubblicità si ripaga" value={p.value} note={p.note} />
      <Result testId="sim-sales" label="Vendite alle aziende, anno 1" value={round100(r.salesY1)} note={`piano consigliato: ${round100(ref.salesY1)}`} />
      <Result testId="sim-spend" label="Spesa totale anno 1" value={round100(r.spendY1)} note="PartnerTech + pubblicità + restyling negozi" />
      <Result testId="sim-total24" tone={r.total24 < ref.total24 - 5000 ? "warn" : "ok"} label="Guadagno netto in 2 anni" value={round100(r.total24)} note={`piano consigliato: ${round100(ref.total24)}`} />
    </div>
  );
};

const GUIDE = [
  <><strong>Sotto circa € 1.200 al mese (zona KO)</strong> la linea del grafico resta sotto lo zero: si spende ogni mese, ma si compare troppo poco per vendere.</>,
  <><strong>Intorno a € 1.200–1.300 (zona a rischio, la striscia gialla)</strong> qualcosa torna, ma lentamente: la pubblicità impiega più di un anno a ripagarsi.</>,
  <><strong>Tra circa € 1.400 e € 3.000 (zona consigliata)</strong> la pubblicità si ripaga in circa un anno, Natale compreso, e poi diventa guadagno.</>,
  <><strong>Oltre € 3.000</strong> si vende di più, ma ogni euro aggiunto rende meno del precedente.</>,
  <>I risparmi del gestionale (meno sprechi e meno ore di ufficio) <strong>arrivano comunque</strong>, anche senza pubblicità. Per questo il guadagno in 2 anni non va mai a zero: chi spende poco perde soprattutto la crescita.</>,
];

const Guide = () => (
  <>
    <H2>Come leggere il simulatore</H2>
    <Bullets items={GUIDE} />
    <Callout title="In una frase">
      Spendere <strong>poco</strong> in pubblicità è peggio che non spenderne affatto. Conviene investire almeno <strong>€ 1.500 al mese</strong>, oppure aspettare e concentrare il budget nei mesi di Pasqua e Natale.
    </Callout>
    <p className="text-xs italic text-[var(--ink-soft)] mt-2">
      Stime basate sulle ipotesi prudenti del documento. Il simulatore usa un budget fisso ogni mese per 24 mesi e include anche le vendite online con spedizione, quindi i risultati possono differire leggermente dalle tabelle delle sezioni 5 e 6.
    </p>
  </>
);

export const Simulator = () => {
  const { M, setM, opt, setOpt, r, ref, z, series } = useSimulator();
  return (
    <Section id="simulatore" num="08" kicker="Simulatore" title="Cosa succede se si spende meno in pubblicità?">
      <P>
        Spostate il cursore e guardate come cambiano i numeri. Il punto da capire è semplice: <strong>la pubblicità online funziona come un'asta</strong>.
        Chi offre troppo poco non compare tra i primi risultati, e chi non compare non viene cliccato.
        Sotto una certa soglia i soldi si spendono comunque, ma i clienti non arrivano.
      </P>
      <Controls M={M} setM={setM} opt={opt} setOpt={setOpt} />
      <div className={`sim-zone-box sim-zone-box--${z.key} avoid-break`} data-testid="sim-zone">
        <div className="kicker !text-current">{z.label}</div>
        <div className="text-[15px] leading-relaxed mt-1">{z.text}</div>
      </div>
      <Results r={r} ref={ref} z={z} M={M} />
      <Figure caption="Il grafico mostra quanto la pubblicità ha fatto guadagnare, meno quanto è costata, mese dopo mese. Sotto la linea dorata dello zero si sta ancora pagando; sopra si guadagna.">
        <LineChart testId="sim-chart" series={series} labels={labels} />
        <Legend items={series} />
      </Figure>
      <Guide />
    </Section>
  );
};
